import { type DeepKeys } from "@tanstack/react-form";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";

import { Button } from "@/shared/ui/button";
import { Textarea } from "@/shared/ui/textarea";

import type { FormApi } from "./types";

import { FormFieldWrapper } from "./form-field-wrapper";
import { maskValue } from "./mask-value";

/* eslint-disable @typescript-eslint/no-explicit-any */
type FormTextareaProps<TFormData> = {
  form: FormApi<TFormData>;
  name: DeepKeys<TFormData>;
  label: string;
  placeholder?: string;
  rows?: number;
  required?: boolean;
  description?: string;
  className?: string;
  onBlur?: () => void;
  type?: "text" | "secret";
};

export function FormTextarea<TFormData>({
  form,
  name,
  label,
  placeholder,
  rows = 3,
  required = false,
  description,
  className,
  onBlur,
  type = "text",
}: FormTextareaProps<TFormData>) {
  const [isVisible, setIsVisible] = useState(false);
  const isSecret = type === "secret";

  return (
    <FormFieldWrapper
      form={form}
      name={name}
      label={label}
      required={required}
      description={description ?? null}
      className={className ?? null}
    >
      {(field, isInvalid) => (
        <div className="relative">
          {isSecret && !isVisible ? (
            <div
              id={field.name}
              className="flex min-h-16 w-full rounded-none border border-input bg-transparent px-2.5 py-2 text-xs break-all"
              style={
                /* oxlint-disable shadcn/no-inline-styles -- textarea height is computed from the requested row count. */
                { minHeight: `${rows * 20 + 16}px` }
                /* oxlint-enable shadcn/no-inline-styles */
              }
              aria-label="Hidden secret value"
            >
              {maskValue(
                (field.state.value as string) ?? "",
                "textarea"
              )}
            </div>
          ) : (
            <Textarea
              id={field.name}
              name={field.name}
              placeholder={placeholder}
              value={(field.state.value as string) ?? ""}
              onChange={(e) => {
                field.handleChange(e.target.value as any);
              }}
              onBlur={() => {
                field.handleBlur();
                onBlur?.();
              }}
              aria-invalid={isInvalid}
              rows={rows}
              autoComplete={isSecret ? "off" : undefined}
            />
          )}

          {isSecret && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2"
              onClick={() => setIsVisible(!isVisible)}
            >
              {isVisible ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      )}
    </FormFieldWrapper>
  );
}
